import { useState, useEffect, useCallback } from 'react';
import { X, Mail, RefreshCw, RotateCcw, AlertTriangle, Check, Eye } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATE_LABELS = {
  A1: 'A1 · Advisor — solicitud recibida (auto)',
  A2: 'A2 · Advisor — perfil activado (manual)',
  A3: 'A3 · Advisor — no admitido (manual)',
  A4: 'A4 · Advisor — perfil en reserva (manual)',
  E1: 'E1 · Empresa — solicitud recibida (auto)',
  E2: 'E2 · Empresa — Top 3 disponible (manual)',
};

// Reusable preview + optional confirm-send modal.
export function EmailPreviewModal({ token, template, nombre, referencia, entityType, entityId, onClose, onSent }) {
  const [data, setData] = useState(null);
  const [view, setView] = useState('html');
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null);
  const canSend = !!entityId && ['A2', 'A3', 'E2'].includes(template);

  useEffect(() => {
    (async () => {
      const p = new URLSearchParams({ template });
      if (nombre) p.set('nombre', nombre);
      if (referencia) p.set('referencia', referencia);
      const res = await fetch(`${BACKEND_URL}/api/admin/email/preview?${p}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setData(await res.json());
    })();
  }, [token, template, nombre, referencia]);

  const doSend = async () => {
    setSending(true); setMsg(null);
    const res = await fetch(`${BACKEND_URL}/api/admin/email/send-manual`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ template, entity_type: entityType, entity_id: entityId, referencia }),
    });
    const j = await res.json();
    if (res.ok) { setMsg({ type: 'ok', text: 'Correo enviado' }); onSent && onSent(); }
    else setMsg({ type: 'err', text: j.detail || 'Error' });
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4 py-8" data-testid="email-preview-modal">
      <div className="w-full max-w-2xl bg-[#0d1b2a] border border-white/10 rounded-lg flex flex-col max-h-full">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <h3 className="text-white text-sm font-medium flex items-center gap-2"><Mail size={15} />{TEMPLATE_LABELS[template] || template}</h3>
          <div className="flex items-center gap-3">
            <button onClick={() => setView(view === 'html' ? 'text' : 'html')} className="text-white/40 hover:text-white text-xs flex items-center gap-1"><Eye size={12} />{view === 'html' ? 'Ver texto plano' : 'Ver HTML'}</button>
            <button onClick={onClose} className="text-white/40 hover:text-white"><X size={18} /></button>
          </div>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {!data ? <p className="text-white/30 text-sm py-8 text-center">Cargando…</p> : (
            <>
              <p className="text-white/40 text-xs mb-2">Asunto: <span className="text-white/80">{data.subject}</span></p>
              {view === 'html'
                ? <iframe title="preview" srcDoc={data.html} className="w-full h-[440px] bg-white rounded" data-testid="email-preview-iframe" />
                : <pre className="text-white/70 text-xs whitespace-pre-wrap bg-black/20 rounded p-3">{data.text}</pre>}
            </>
          )}
        </div>
        {canSend && (
          <div className="flex items-center gap-3 px-5 py-3 border-t border-white/10">
            <button onClick={doSend} disabled={sending} data-testid="email-confirm-send"
              className="bg-[#C58C40] text-white rounded px-4 py-2 text-xs tracking-wide hover:bg-[#b07b35] disabled:opacity-50">
              {sending ? 'Enviando…' : 'Confirmar y enviar'}
            </button>
            {msg && <span className={`text-xs ${msg.type === 'ok' ? 'text-emerald-400' : 'text-red-400'}`}>
              {msg.type === 'ok' ? <Check size={12} className="inline mr-1" /> : <AlertTriangle size={12} className="inline mr-1" />}{msg.text}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

// "Correos" subtab: send log + template testing (no send).
export function CorreosTab({ token }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [retrying, setRetrying] = useState(null);
  const [retryMsg, setRetryMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${BACKEND_URL}/api/admin/email/logs`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setLogs((await res.json()).logs);
    setLoading(false);
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const retry = async (id) => {
    setRetrying(id); setRetryMsg(null);
    const res = await fetch(`${BACKEND_URL}/api/admin/email/retry/${id}`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` },
    });
    const j = await res.json().catch(() => ({}));
    setRetryMsg(res.ok ? { type: 'ok', text: 'Reenviado correctamente' } : { type: 'err', text: j.detail || 'El reintento falló' });
    setRetrying(null);
    load();
  };

  return (
    <div>
      <div className="mb-6">
        <p className="text-white/50 text-xs font-medium mb-2">Previsualizar plantillas (sin enviar)</p>
        <div className="flex flex-wrap gap-2">
          {Object.keys(TEMPLATE_LABELS).map((t) => (
            <button key={t} onClick={() => setPreview({ template: t })} data-testid={`preview-template-${t}`}
              className="bg-white/5 border border-white/10 rounded px-3 py-1.5 text-white/70 hover:text-white text-xs">
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <p className="text-white/50 text-xs font-medium">Registro de envíos</p>
        <div className="flex items-center gap-3">
          {retryMsg && <span className={`text-xs ${retryMsg.type === 'ok' ? 'text-emerald-400' : 'text-red-400'}`}>{retryMsg.text}</span>}
          <button onClick={load} className="p-1.5 rounded hover:bg-white/10 text-white/50"><RefreshCw size={14} className={loading ? 'animate-spin' : ''} /></button>
        </div>
      </div>
      <div className="border border-white/5 rounded overflow-x-auto" data-testid="email-logs-table">
        <table className="w-full text-xs">
          <thead className="bg-white/[0.03] border-b border-white/5 text-white/50">
            <tr>
              <th className="px-3 py-2.5 text-left font-medium">Fecha</th>
              <th className="px-3 py-2.5 text-left font-medium">Plantilla</th>
              <th className="px-3 py-2.5 text-left font-medium">Destinatario</th>
              <th className="px-3 py-2.5 text-left font-medium">Estado</th>
              <th className="px-3 py-2.5 text-left font-medium">Detalle</th>
              <th className="px-3 py-2.5 text-left font-medium">Acción</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 ? (
              <tr><td colSpan={6} className="text-white/30 text-center py-10">Sin envíos registrados</td></tr>
            ) : logs.map((l) => (
              <tr key={l.id} className="border-b border-white/5">
                <td className="px-3 py-2 text-white/50">{(l.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                <td className="px-3 py-2 text-white/70 font-mono">{l.template}</td>
                <td className="px-3 py-2 text-white/60">{l.to}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 rounded text-[11px] ${l.status === 'sent' ? 'text-emerald-400 bg-emerald-400/10' : 'text-red-400 bg-red-400/10'}`}>{l.status === 'sent' ? 'Enviado' : 'Fallido'}{l.attempts > 1 ? ` (${l.attempts})` : ''}</span>
                </td>
                <td className="px-3 py-2 text-white/40">{l.error ? l.error.slice(0, 60) : (l.provider_id || '—')}</td>
                <td className="px-3 py-2">
                  {l.status === 'failed' && (
                    <button onClick={() => retry(l.id)} disabled={retrying === l.id} data-testid={`email-retry-${l.id}`}
                      className="flex items-center gap-1 text-[#C58C40] hover:text-[#e0a862] disabled:opacity-50">
                      <RotateCcw size={12} className={retrying === l.id ? 'animate-spin' : ''} />Reenviar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {preview && (
        <EmailPreviewModal token={token} template={preview.template} onClose={() => setPreview(null)} />
      )}
    </div>
  );
}
